import { useCallback, useState } from 'react';
import { getAccessToken } from '../services/client';
import * as subscriptions from '../services/subscriptions';
import { ApiError } from '../services/types';
import useAsync, { Async } from './useAsync';

export type UseSubscription = {
  plans: Async<subscriptions.SubscriptionPlan[]>;
  active: Async<subscriptions.ActiveSubscriptionPerks>;
  /** True when a live plan is held. The ONLY proof of membership. */
  isMember: boolean;
  /**
   * False when there is no session, so the screen can say "sign in to see
   * plans" instead of blaming the network for a 401.
   */
  signedIn: boolean;
  /** True while a purchase or cancellation is in flight. */
  working: boolean;
  actionError: string | null;
  /**
   * Step one of buying: creates a `pending_payment` subscription.
   *
   * Resolves with the checkout details when `paymentMode` is `online`, so the
   * caller can hand them straight to Razorpay. Resolves null for cash - there
   * is nothing to open - and on failure.
   *
   * Grants nothing on its own. Membership only becomes true after the payment
   * is confirmed and `active` is re-read, which `refreshAfterPayment` does.
   */
  buy: (
    planId: string,
    paymentMode?: subscriptions.SubscriptionPaymentMode,
  ) => Promise<subscriptions.SubscriptionCheckout | null>;
  cancel: (subscriptionId: string, reason: string) => Promise<boolean>;
  /** Call once the gateway reports success. */
  refreshAfterPayment: () => void;
  reload: () => void;
};

export function useSubscription(cityId?: string): UseSubscription {
  /*
   * Both endpoints sit behind `JwtAuthGuard` on the server, so without a token
   * they answer 401 - and `useAsync` reports that as an ordinary error.
   *
   * That is what produced "Plans aren't loading right now / Check your
   * connection and try again" on a perfectly good connection: the request was
   * refused for want of a session, the copy blamed the network, and Retry
   * re-sent the same unauthenticated request, so it could never come good.
   *
   * Not reactive, and does not need to be - the same reasoning as `DriverCard`:
   * signing in re-renders this tree from `HomeScreen`, which is exactly when
   * the answer changes.
   */
  const signedIn = !!getAccessToken();

  const plans = useAsync(() => subscriptions.plans(cityId), [cityId], {
    enabled: signedIn,
  });
  const active = useAsync(() => subscriptions.active(), [], {
    enabled: signedIn,
  });

  const [working, setWorking] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const buy = useCallback(
    async (
      planId: string,
      paymentMode: subscriptions.SubscriptionPaymentMode = 'online',
    ): Promise<subscriptions.SubscriptionCheckout | null> => {
      setWorking(true);
      setActionError(null);
      try {
        const created = await subscriptions.purchase(planId, paymentMode);
        if (paymentMode !== 'online') {
          // Nothing to open. The plan waits on ops to confirm payment.
          active.reload();
          return null;
        }
        return await subscriptions.checkout(created.id);
      } catch (thrown) {
        setActionError(messageFor(thrown));
        return null;
      } finally {
        setWorking(false);
      }
    },
    [active],
  );

  const cancel = useCallback(
    async (subscriptionId: string, reason: string): Promise<boolean> => {
      setWorking(true);
      setActionError(null);
      try {
        await subscriptions.cancel(subscriptionId, reason);
        active.reload();
        return true;
      } catch (thrown) {
        setActionError(messageFor(thrown));
        return false;
      } finally {
        setWorking(false);
      }
    },
    [active],
  );

  const reload = useCallback(() => {
    plans.reload();
    active.reload();
  }, [plans, active]);

  return {
    plans,
    active,
    isMember: !!active.data?.subscriptionId,
    signedIn,
    working,
    actionError,
    buy,
    cancel,
    refreshAfterPayment: active.reload,
    reload,
  };
}

function messageFor(thrown: unknown): string {
  if (thrown instanceof ApiError) {
    if (thrown.message) {
      return thrown.message;
    }
    if (thrown.status === 409) {
      return 'You already have an active plan.';
    }
    if (thrown.status === 0) {
      return 'Check your connection and try again.';
    }
  }
  return 'Something went wrong. Try again.';
}

export default useSubscription;
